import { Controller } from '@hotwired/stimulus'
import { createPopper } from '@popperjs/core'
import { fetchBotFunctions } from '../lib/bot_functions_cache'
import '../../assets/stylesheets/components/function_chip.css'

const CONTAINER_SELECTOR = '.toastui-editor-ww-container .toastui-editor-contents'
const FALLBACK_CONTAINER_SELECTOR = '.toastui-editor-contents'
const RETRY_INTERVAL = 300
const MAX_RETRIES = 15

// Handles click-to-show popover on action chip widgets.
// Editor mode: widgets are rendered by widgetRules, this controller adds click popovers.
// Readonly mode: does DOM scanning to render chips in static HTML.
export default class extends Controller {
  static values = {
    readonly: { type: Boolean, default: false }
  }

  connect() {
    this.functionMap = null
    this.popover = null
    this.popperInstance = null
    this.handleDismiss = this.dismissPopover.bind(this)
    this.handleChipClick = this.onChipClick.bind(this)
    this.initWithRetry()
  }

  disconnect() {
    this.removePopover()
    this.containerElement?.removeEventListener('click', this.handleChipClick, true)
  }

  // --- Initialization ---

  initWithRetry(retries = 0) {
    if (!this.findContainer() && retries < MAX_RETRIES) {
      setTimeout(() => this.initWithRetry(retries + 1), RETRY_INTERVAL)
      return
    }
    this.initialize()
  }

  findContainer() {
    const next = this.element.nextElementSibling
    return next?.querySelector(CONTAINER_SELECTOR) || next?.querySelector(FALLBACK_CONTAINER_SELECTOR)
  }

  async initialize() {
    let functions
    try {
      functions = await fetchBotFunctions()
      this.functionMap = new Map(functions.map(f => [f.key, f]))
    } catch {
      this.functionMap = new Map()
      return
    }

    if (this.element.functionMap) this.functionMap = this.element.functionMap

    const container = this.findContainer()
    if (!container) return

    this.containerElement = container
    container.addEventListener('click', this.handleChipClick, true)

    if (this.readonlyValue && functions.length > 0) {
      this.highlightChipsInViewer(container, functions)
    }
  }

  // --- Viewer mode: DOM scanning ---

  highlightChipsInViewer(container, functions) {
    const pattern = this.buildFunctionPattern(functions)
    const textNodes = this.collectMatchingTextNodes(container, pattern)

    for (let i = textNodes.length - 1; i >= 0; i--) {
      this.replaceTextWithChips(textNodes[i], pattern)
    }
  }

  buildFunctionPattern(functions) {
    const escaped = functions
      .map(f => f.key)
      .sort((a, b) => b.length - a.length)
      .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    return new RegExp(`\\b(${escaped.join('|')})\\b`, 'g')
  }

  collectMatchingTextNodes(container, pattern) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) =>
        node.parentElement.closest('.function-chip, code, pre')
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT
    })

    const nodes = []
    let node
    while ((node = walker.nextNode())) {
      pattern.lastIndex = 0
      if (pattern.test(node.textContent)) nodes.push(node)
      pattern.lastIndex = 0
    }
    return nodes
  }

  replaceTextWithChips(textNode, pattern) {
    const text = textNode.textContent
    const fragment = document.createDocumentFragment()
    let lastIndex = 0

    pattern.lastIndex = 0
    let match
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)))
      }

      const key = match[1]
      const chip = document.createElement('span')
      chip.className = 'function-chip'
      chip.dataset.functionKey = key
      chip.textContent = key

      const func = this.functionMap.get(key)
      if (func && !func.available) chip.classList.add('function-chip--unavailable')

      fragment.appendChild(chip)
      lastIndex = pattern.lastIndex
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.substring(lastIndex)))
    }
    if (fragment.childNodes.length > 0) {
      textNode.parentNode.replaceChild(fragment, textNode)
    }
  }

  // --- Popover ---

  onChipClick(event) {
    const chip = event.target.closest('.function-chip')
    if (!chip) return

    event.preventDefault()
    event.stopPropagation()
    this.showPopover(chip)

    // In editor mode, place cursor right after the widget so backspace deletes it
    if (!this.readonlyValue) {
      this.selectChipInEditor(chip)
    }
  }

  // Place the ProseMirror cursor right after the widget node so that
  // pressing backspace naturally deletes the chip.
  selectChipInEditor(chipElement) {
    const editor = this.element.editorInstance
    const wwEditor = editor?.wwEditor || editor?.getCurrentModeEditor?.()
    if (!wwEditor?.view) return

    const view = wwEditor.view
    // The chip is inside a .tui-widget wrapper rendered by widgetNodeView.
    // posAtDOM on the widget's parent at the widget's sibling offset gives
    // us the position just before the widget node.
    const widgetDOM = chipElement.closest('.tui-widget')
    if (!widgetDOM) return

    const parent = widgetDOM.parentNode
    if (!parent) return

    const offset = Array.from(parent.childNodes).indexOf(widgetDOM)
    if (offset === -1) return

    // posAtDOM(parent, offset) = position before the widget
    // posAtDOM(parent, offset + 1) = position after the widget
    const afterPos = view.posAtDOM(parent, offset + 1)
    if (afterPos == null) return

    wwEditor.setSelection(afterPos)
  }

  showPopover(chipElement) {
    this.removePopover()

    const func = this.functionMap?.get(chipElement.dataset.functionKey)
    if (!func) return

    this.popover = document.createElement('div')
    this.popover.className = 'function-chip-popover'
    this.popover.innerHTML = this.buildPopoverHTML(func)
    document.body.appendChild(this.popover)

    this.popperInstance = createPopper(chipElement, this.popover, {
      placement: 'bottom-start',
      modifiers: [
        { name: 'offset', options: { offset: [0, 8] } },
        { name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] } },
        { name: 'preventOverflow', options: { padding: 16 } }
      ]
    })

    requestAnimationFrame(() => document.addEventListener('click', this.handleDismiss))
  }

  buildPopoverHTML(func) {
    const statusClass = func.available ? 'function-chip-popover__status--available' : 'function-chip-popover__status--unavailable'
    const statusText = func.available ? 'Available' : 'Unavailable'

    let html = `
      <div class="function-chip-popover__header">
        <span class="function-chip-popover__name">${this.esc(func.display_name)}</span>
        <span class="function-chip-popover__status ${statusClass}">${statusText}</span>
      </div>`

    if (func.category || func.namespace) {
      html += `<div class="function-chip-popover__category">${this.esc(func.category || func.namespace)}</div>`
    }

    html += this.buildRequirementSection(func.context_requirements, 'Context Requirements')
    html += this.buildRequirementSection(func.missing_requirements, 'Missing Configuration', 'function-chip-popover__section--warning')

    if (func.settings_url) {
      html += `
        <div class="function-chip-popover__footer">
          <a href="${this.esc(func.settings_url)}" target="_blank" rel="noopener" class="function-chip-popover__link">
            ${func.settings_label || 'Configure Policies'} &rarr;
          </a>
        </div>`
    }

    return html
  }

  buildRequirementSection(requirements, title, extraClass = '') {
    if (!requirements?.length) return ''
    const items = requirements.map(r => `<li>${this.esc(Array.isArray(r) ? r.join(', ') : r)}</li>`).join('')
    return `
      <div class="function-chip-popover__section ${extraClass}">
        <div class="function-chip-popover__section-title">${title}</div>
        <ul class="function-chip-popover__list">${items}</ul>
      </div>`
  }

  dismissPopover(event) {
    if (this.popover && !this.popover.contains(event.target) && !event.target.closest('.function-chip')) {
      this.removePopover()
    }
  }

  removePopover() {
    if (this.popperInstance) {
      this.popperInstance.destroy()
      this.popperInstance = null
    }
    if (this.popover) {
      this.popover.remove()
      this.popover = null
    }
    document.removeEventListener('click', this.handleDismiss)
  }

  // --- Utilities ---

  esc(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
